import React, { useState, useEffect, useCallback , useMemo} from 'react';
import {
    Box, VStack, HStack, Heading, Button, Table, Thead, Tbody, Tr, Th, Td,
    TableContainer, Input, InputGroup, InputLeftElement, Select, Card, CardBody,
    CardHeader, Badge, IconButton, Menu, MenuButton, MenuList, MenuItem,
    useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, ModalCloseButton, FormControl, FormLabel, Spinner, Alert, AlertIcon,
    useColorModeValue, Flex, Text, useToast
} from '@chakra-ui/react';
import { MdSearch, MdAdd, MdEdit, MdDelete, MdMoreVert, MdRefresh } from 'react-icons/md';
import { TbChecklist, TbClock } from 'react-icons/tb';
import apiService from '../services/apiService';
import { ToastManager } from '../components/Common/ToastManager';
import { useSearchParams } from 'react-router-dom';
import SqlEditor from '../components/Common/SQLEditor';



// ── Helpers ───────────────────────────────────────────────────────────────────
const parse24hTo12h = (time24) => {
    if (!time24) return { hour: '08', minute: '00', period: 'AM' };
    const [h, m] = time24.split(':').map(Number);
    return {
        period: h >= 12 ? 'PM' : 'AM',
        hour:   ((h % 12) || 12).toString().padStart(2, '0'),
        minute: m.toString().padStart(2, '0'),
    };
};

const to24h = ({ hour, minute, period }) => {
    let h = parseInt(hour, 10);
    if (period === 'AM' && h === 12) h = 0;
    if (period === 'PM' && h !== 12) h += 12;
    return `${h.toString().padStart(2, '0')}:${minute}`;
};

const formatDisplayTime = (time24) => {
    if (!time24) return '—';
    const { hour, minute, period } = parse24hTo12h(time24);
    return `${parseInt(hour, 10)}:${minute} ${period}`;
};

  const formatDateTime = d => {
    if (!d) return 'N/A';
    const date = new Date(d.replace(' ', 'T').split('.')[0] + 'Z');
    return isNaN(date) ? 'Invalid Date' : date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };



const HOURS   = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
const MINUTES = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];

const TIMEZONES = [
    { label: 'UTC',                    value: 'UTC' },
    { label: 'IST — India (UTC+5:30)', value: 'Asia/Kolkata' },
    { label: 'EST — New York (UTC-5)', value: 'America/New_York' },
    { label: 'CST — Chicago (UTC-6)',  value: 'America/Chicago' },
    { label: 'MST — Denver (UTC-7)',   value: 'America/Denver' },
    { label: 'PST — Los Angeles (UTC-8)', value: 'America/Los_Angeles' },
    { label: 'GMT — London (UTC+0)',   value: 'Europe/London' },
    { label: 'CET — Paris (UTC+1)',    value: 'Europe/Paris' },
    { label: 'GST — Dubai (UTC+4)',    value: 'Asia/Dubai' },
    { label: 'SGT — Singapore (UTC+8)',value: 'Asia/Singapore' },
    { label: 'JST — Tokyo (UTC+9)',    value: 'Asia/Tokyo' },
    { label: 'AEST — Sydney (UTC+10)',  value: 'Australia/Sydney' },
];


// ── TimePicker ────────────────────────────────────────────────────────────────
const TimePicker = ({ value, onChange, inputBg, borderColor, headingColor, isInvalid }) => {
    const { hour, minute, period } = parse24hTo12h(value);
    const update = (field, val) => onChange(to24h({ hour, minute, period, [field]: val }));
    const sx = { bg: inputBg, borderColor: isInvalid ? 'red.400' : borderColor, color: headingColor, size: 'md' };

    return (
        <HStack spacing={2}>
            <Select value={hour}   onChange={e => update('hour',   e.target.value)} {...sx} maxW="90px">
                {HOURS.map(h => <option key={h} value={h}>{parseInt(h, 10)}</option>)}
            </Select>
            <Text color={headingColor} fontWeight="bold" fontSize="lg">:</Text>
            <Select value={minute} onChange={e => update('minute', e.target.value)} {...sx} maxW="90px">
                {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
            </Select>
            <Select value={period} onChange={e => update('period', e.target.value)} {...sx} maxW="90px">
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </Select>
        </HStack>
    );
};

// ── Main component ────────────────────────────────────────────────────────────
function ChecklistMapping({ userProfile }) {

    const [mappings,         setMappings]         = useState([]);
    const [filteredMappings, setFilteredMappings] = useState([]);
    const [clients,          setClients]          = useState([]);
    const [checklists,       setChecklists]       = useState([]);
    const [loading,          setLoading]          = useState(true);
    const [searchTerm,       setSearchTerm]       = useState('');
    const [clientFilter,     setClientFilter]     = useState('all');
    const [checklistFilter,  setChecklistFilter]  = useState('all');
    const [currentMapping,   setCurrentMapping]   = useState(null);
    const [isEditMode,       setIsEditMode]       = useState(false);
   
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [viewTitle, setViewTitle]   = useState('');
    const [viewSql, setViewSql]       = useState('');
    const toast = useToast();


    // No auto-detection — user must explicitly select timezone

    const emptyForm = { checklistId: '', clientId: '', scheduledTime: '09:00', timezone: '' };
    const [formData,   setFormData]   = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});

    const { isOpen: isFormOpen,   onOpen: onFormOpen,   onClose: onFormClose   } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    // ── Theme ─────────────────────────────────────────────────────────────────
    const cardBg       = useColorModeValue('white',    'gray.800');
    const borderColor  = useColorModeValue('gray.200', 'gray.700');
    const textColor    = useColorModeValue('gray.600', 'gray.300');
    const headingColor = useColorModeValue('gray.800', 'white');
    const hoverBg      = useColorModeValue('gray.50',  'gray.700');
    const inputBg      = useColorModeValue('white',    'gray.700');
    const mutedColor   = useColorModeValue('gray.500', 'gray.400');
    const tableHeadBg  = useColorModeValue('gray.50',  'gray.900');
    const [searchParams] = useSearchParams();


      const sqlEditorBg    = useColorModeValue('gray.900',  'gray.950');
      const sqlEditorBorder= useColorModeValue('gray.600',  'gray.700');
      const sqlEditorHdrBg = useColorModeValue('gray.800',  'gray.900');
      const sqlEditorHdrBd = useColorModeValue('gray.600',  'gray.700');
      const sqlTagBg       = useColorModeValue('blue.600',  'blue.500');
      const sqlCharColor   = useColorModeValue('gray.400',  'gray.500');
      const sqlPlaceholder = useColorModeValue('gray.600',  'gray.600');
      const viewSqlBodyBg  = useColorModeValue('gray.900',  'gray.950');
      const copyBtnBg      = useColorModeValue('gray.700',  'gray.600');
      const copyBtnHoverBg = useColorModeValue('gray.600',  'gray.500');
    
      // Stable token bundle passed to SqlEditor
      const sqlTokens = {
        sqlEditorBg, sqlEditorBorder, sqlEditorHdrBg, sqlEditorHdrBd,
        sqlTagBg, sqlCharColor, sqlPlaceholder, viewSqlBodyBg, copyBtnBg, copyBtnHoverBg,
      };

      
    const openViewQuery = (title, sqlText) => {
    setViewTitle(title || 'Query');
    setViewSql(sqlText || '-- No query found --');
    setIsViewOpen(true);
    };

    const closeViewQuery = () => setIsViewOpen(false);


    const availableClients = useMemo(() => {
        if (!formData.checklistId) return [];         
        const mappedClientIds = new Set(
            mappings
                .filter(m => m.checklistId?.toString() === formData.checklistId)
                .map(m => m.clientId?.toString())
        );
        return clients.filter(c => !mappedClientIds.has(c.clientId?.toString()));
    }, [clients, mappings, formData.checklistId]);

    const isAdminuser = () => {
        return userProfile && 
               (userProfile.role === 'admin' || userProfile.role === 'devops');
    };

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [mappingsRes, clientsRes, checklistsRes] = await Promise.all([
                apiService.getChecklistMappings(),
                apiService.getClientsList({ size: 1000 }),
                apiService.fetchChecklists(),
            ]);
            if (mappingsRes.success && mappingsRes.data)
                setMappings(Array.isArray(mappingsRes.data) ? mappingsRes.data : mappingsRes.data.content || []);
            if (clientsRes.success && clientsRes.data)
                setClients(clientsRes.data.content || []);
            if (checklistsRes.success && checklistsRes.data)
                setChecklists(Array.isArray(checklistsRes.data) ? checklistsRes.data : []);
        } catch {
            ToastManager.showError('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Filter ────────────────────────────────────────────────────────────────
    useEffect(() => {
        let result = [...mappings];
        if (searchTerm.trim()) {
            const q = searchTerm.toLowerCase();
            result = result.filter(m =>
                m.clientName?.toLowerCase().includes(q) ||
                m.checklistName?.toLowerCase().includes(q)
            );
        }
        if (clientFilter    !== 'all') result = result.filter(m => m.clientId?.toString()    === clientFilter);
        if (checklistFilter !== 'all') result = result.filter(m => m.checklistId?.toString() === checklistFilter);
        setFilteredMappings(result);
    }, [mappings, searchTerm, clientFilter, checklistFilter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    
    useEffect(() => {
        const clientId = searchParams.get('clientId');
        if (clientId ) {
            setClientFilter(clientId.toString());
        }
        const checklistId= searchParams.get("checklistId");
        if(checklistId){
            setChecklistFilter(checklistId.toString())
        }
    }, [searchParams]);


    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        const e = {};
        if (!formData.checklistId)   e.checklistId   = 'Please select a checklist.';
        if (!formData.clientId)      e.clientId      = 'Please select a client.';
        if (!formData.scheduledTime) e.scheduledTime = 'Please set a scheduled time.';
        if (!formData.timezone)      e.timezone      = 'Please select a timezone.';  // ← added
        return e;
    };

    // ── Modal helpers ─────────────────────────────────────────────────────────
    const handleOpenCreate = () => {
        setIsEditMode(false); setCurrentMapping(null);
        setFormData(emptyForm); setFormErrors({});
        onFormOpen();
    };

    const handleOpenEdit = (mapping) => {
        setIsEditMode(true); setCurrentMapping(mapping);
        setFormData({
            checklistId:   mapping.checklistId?.toString() || '',
            clientId:      mapping.clientId?.toString()    || '',
            scheduledTime: mapping.scheduledTime           || '09:00',
            timezone:      mapping.timezone  || 'UTC'
        });
        setFormErrors({});
        onFormOpen();
    };

    const handleFormClose = () => {
        setFormData(emptyForm); setFormErrors({});
        setCurrentMapping(null); setIsEditMode(false);
        onFormClose();
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        const errors = validate();
        if (Object.keys(errors).length) { setFormErrors(errors); return; }
        try {
            const payload = {
                checklistId:   parseInt(formData.checklistId, 10),
                clientId:      parseInt(formData.clientId, 10),
                scheduledTime: formData.scheduledTime, 
                updatedBy: userProfile?.email,
                timezone: formData.timezone
            };
            const res = isEditMode
                ? await apiService.updateChecklistMapping(currentMapping.id, payload)
                : await apiService.createChecklistMapping(payload);

            if (res.success) {
                ToastManager.showSuccess(isEditMode ? 'Mapping updated successfully' : 'Mapping created successfully');
                handleFormClose(); fetchAll();
            } else {
                ToastManager.showError(res.message || 'Operation failed');
            }
        } catch {
            ToastManager.showError('Operation failed');
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleOpenDelete = (mapping) => { setCurrentMapping(mapping); onDeleteOpen(); };

    const handleConfirmDelete = async () => {
        try {
            const res = await apiService.deleteChecklistMapping(currentMapping.id);
            if (res.success) {
                ToastManager.showSuccess('Mapping deleted successfully');
                onDeleteClose(); setCurrentMapping(null); fetchAll();
            } else {
                ToastManager.showError(res.message || 'Failed to delete');
            }
        } catch {
            ToastManager.showError('Failed to delete mapping');
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Box>
            <VStack spacing={6} align="stretch">

                {/* ── Header ── */}
                <Flex justify="space-between" align="center">
                    <Box>
                        <Heading size="lg" color={headingColor}>Checklist Mapping</Heading>
                        <Text color={textColor} mt={1} fontSize="sm">
                            Map checklists to clients with a daily scheduled run time and timezone
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Button leftIcon={<MdRefresh />} onClick={fetchAll} variant="outline" size="sm">Refresh</Button>
                        {
                            isAdminuser() &&(<Button leftIcon={<MdAdd />} onClick={handleOpenCreate} colorScheme="blue" size="sm">Create Mapping</Button>)
                        }  
                    </HStack>
                </Flex>

                {/* ── Filters ── */}
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardBody>
                        <Flex gap={4} align="center" wrap="wrap">
                            <InputGroup flex="1" minW="220px" maxW="380px">
                                <InputLeftElement pointerEvents="none"><MdSearch color={mutedColor} /></InputLeftElement>
                                <Input
                                    placeholder="Search by client or checklist name..."
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    bg={inputBg} borderColor={borderColor} color={headingColor}
                                    _placeholder={{ color: mutedColor }}
                                />
                            </InputGroup>
                            <FormControl maxW="220px">
                                <Select value={checklistFilter} onChange={e => setChecklistFilter(e.target.value)}
                                    bg={inputBg} borderColor={borderColor} color={headingColor}>
                                    <option value="all">All Checklists</option>
                                    {checklists.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl maxW="220px">
                                <Select value={clientFilter} onChange={e => setClientFilter(e.target.value)}
                                    bg={inputBg} borderColor={borderColor} color={headingColor}>
                                    <option value="all">All Clients</option>
                                    {clients.map(c => <option key={c.clientId} value={c.clientId}>{c.clientName}</option>)}
                                </Select>
                            </FormControl>
                            {(searchTerm || clientFilter !== 'all' || checklistFilter !== 'all') && (
                                <Button size="md" variant="outline" colorScheme="purple"
                                    onClick={() => { setSearchTerm(''); setClientFilter('all'); setChecklistFilter('all'); }}>
                                    Reset Filters
                                </Button>
                            )}
                        </Flex>
                    </CardBody>
                </Card>

                {/* ── Table ── */}
                <Card bg={cardBg} borderColor={borderColor} borderWidth="1px">
                    <CardHeader pb={0}>
                        <Flex justify="space-between" align="center">
                            <Heading size="md" color={headingColor}>Mappings</Heading>
                            <Badge colorScheme="blue" variant="subtle" fontSize="sm" px={3} py={1}>
                                {filteredMappings.length} record{filteredMappings.length !== 1 ? 's' : ''}
                            </Badge>
                        </Flex>
                    </CardHeader>
                    <CardBody p={0}>
                        {loading ? (
                            <Flex justify="center" py={16}>
                                <VStack spacing={4}>
                                    <Spinner size="lg" color="blue.500" thickness="3px" />
                                    <Text color={textColor}>Loading mappings...</Text>
                                </VStack>
                            </Flex>
                        ) : filteredMappings.length === 0 ? (
                            <Flex justify="center" py={16}>
                                <VStack spacing={3}>
                                    <Box color={mutedColor}><TbChecklist size={40} /></Box>
                                    <Text color={mutedColor} fontSize="lg">No mappings found</Text>
                                    <Text color={mutedColor} fontSize="sm">
                                        {searchTerm || clientFilter !== 'all' || checklistFilter !== 'all'
                                            ? 'Try adjusting your filters'
                                            : 'Create your first mapping to get started'}
                                    </Text>
                                </VStack>
                            </Flex>
                        ) : (
                            <TableContainer>
                                <Table variant="simple" size="sm">
                                    <Thead bg={tableHeadBg}>
                                        <Tr>
                                            <Th color={headingColor} py={4}>Client Name</Th>
                                            <Th color={headingColor} py={4}>Checklist Name</Th>
                                            <Th color={headingColor} py={4}>Scheduled Time</Th>
                                            <Th color={headingColor} py={4}>Last Updated</Th>
                                            <Th color={headingColor} py={4} w="80px">Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {filteredMappings.map((mapping, idx) => (
                                            <Tr key={mapping.id ?? idx} _hover={{ bg: hoverBg }}>

                                                <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="sm" fontWeight="semibold" color={headingColor}>{mapping.clientName}</Text>
                                                        <Text fontSize="xs" color={mutedColor}>ID: {mapping.clientId}</Text>
                                                    </VStack>
                                                </Td>

                                                <Td py={4} borderColor={borderColor}>
                                                    <HStack spacing={2}>
                                                        <Box color="blue.500"><TbChecklist size={15} /></Box>
                                                        <VStack align="start" spacing={0}>
                                                            <Text fontSize="sm" fontWeight="medium" color={headingColor}>{mapping.checkListName}</Text>
                                                            <Text fontSize="xs" color={mutedColor}>ID: {mapping.checklistId}</Text>
                                                        </VStack>
                                                    </HStack>
                                                </Td>

                                                {/* Time + Timezone */}
                                                <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={1}>
                                                        <HStack spacing={1}>
                                                            <Box color="purple.400"><TbClock size={13} /></Box>
                                                            <Badge fontFamily="mono" fontSize="xs" colorScheme="purple" variant="subtle" px={2} py={1}>
                                                                {formatDisplayTime(mapping.scheduledTime)}
                                                            </Badge>
                                                        </HStack>
                                                        {mapping.timezone && (
                                                            <Text fontSize="xs" color={mutedColor}>{mapping.timezone}</Text>
                                                        )}
                                                    </VStack>
                                                </Td>
                                                <Td py={4} borderColor={borderColor}>
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="xs" color={mutedColor}>{formatDateTime(mapping.updated_at)}</Text>
                                                    </VStack>
                                                </Td>

                                                <Td py={4} borderColor={borderColor}>
                                                    <Menu>
                                                        <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm"
                                                            color={textColor} _hover={{ bg: hoverBg, color: headingColor }} />
                                                        <MenuList>
                                                            
                                                        <MenuItem
                                                            icon={<TbChecklist />}
                                                            onClick={() => openViewQuery(
                                                                // title to show in modal header
                                                                `${mapping.clientName} • ${mapping.checkListName}`,
                                                                // the actual SQL/query string (adjust field name as per your API)
                                                                mapping.checkListQuery || ''
                                                            )}
                                                            >
                                                            View Query
                                                            </MenuItem>
                                                            
                                                            {isAdminuser() && (
                                                                <>
                                                                    <MenuItem icon={<MdEdit />} onClick={() => handleOpenEdit(mapping)}>
                                                                        Edit
                                                                    </MenuItem>
                                                                    <MenuItem icon={<MdDelete />} onClick={() => handleOpenDelete(mapping)} color="red.500">
                                                                        Delete
                                                                    </MenuItem>
                                                                </>
                                                            )}

                                                        </MenuList>
                                                    </Menu>
                                                </Td>

                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardBody>
                </Card>

                {/* ── Create / Edit Modal ── */}
                <Modal isOpen={isFormOpen} onClose={handleFormClose} size="md" isCentered>
                    <ModalOverlay />
                    <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
                            <HStack spacing={3}>
                                <Box color="blue.500"><TbChecklist size={20} /></Box>
                                <Text>{isEditMode ? 'Edit Mapping' : 'Create Mapping'}</Text>
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton color={textColor} />

                        <ModalBody py={6}>
                            <VStack spacing={5} align="stretch">

                                {/* Checklist */}
                                <FormControl isRequired isInvalid={!!formErrors.checklistId}>
                                    <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Checklist</FormLabel>
                                    <Select placeholder="Select a checklist" value={formData.checklistId} isDisabled={isEditMode}
                                        onChange={e => {
                                            setFormData(p => ({ ...p, checklistId: e.target.value, clientId: '' }));  // ← also clear clientId
                                            setFormErrors(p => ({ ...p, checklistId: '', clientId: '' }));
                                        }}
                                        bg={inputBg} borderColor={borderColor} color={headingColor}>
                                        {checklists.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </Select>
                                    {formErrors.checklistId && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.checklistId}</Text>}
                                </FormControl>

                                {/* Client */}
                                <FormControl isRequired isInvalid={!!formErrors.clientId}>
                                    <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">Client</FormLabel>
                                    <Select
                                        placeholder={
                                            !formData.checklistId
                                                ? 'Select a checklist first'          // ← helpful hint
                                                : availableClients.length === 0
                                                    ? 'All clients already mapped'
                                                    : 'Select a client'
                                        }
                                        value={formData.clientId}
                                        isDisabled={isEditMode || !formData.checklistId}   // ← also disable until checklist is chosen
                                        onChange={e => {
                                            setFormData(p => ({ ...p, clientId: e.target.value }));
                                            setFormErrors(p => ({ ...p, clientId: '' }));
                                        }}
                                        bg={inputBg} borderColor={formErrors.clientId ? 'red.400' : borderColor} color={headingColor}
                                    >
                                        {availableClients.map(c => (
                                            <option key={c.clientId} value={c.clientId}>{c.clientName}</option>
                                        ))}
                                    </Select>
                                    {formErrors.clientId && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.clientId}</Text>}
                                </FormControl>
                                <FormControl isRequired isInvalid={!!formErrors.timezone}>
                                    <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                                        Timezone
                                    </FormLabel>
                                    <Select
                                        placeholder="Select timezone"
                                        value={formData.timezone}
                                        onChange={e => {
                                            setFormData(p => ({ ...p, timezone: e.target.value }));
                                            setFormErrors(p => ({ ...p, timezone: '' }));
                                        }}
                                        bg={inputBg}
                                        borderColor={formErrors.timezone ? 'red.400' : borderColor}
                                        color={headingColor}
                                    >
                                        {TIMEZONES.map(tz => (
                                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                                        ))}
                                    </Select>
                                    {formErrors.timezone && (
                                        <Text fontSize="xs" color="red.500" mt={1}>{formErrors.timezone}</Text>
                                    )}
                                </FormControl>


                                {/* Time Picker */}
                                <FormControl isRequired isInvalid={!!formErrors.scheduledTime}>
                                    <FormLabel color={headingColor} fontSize="sm" fontWeight="medium">
                                        <HStack spacing={2}><TbClock size={15} /><Text>Scheduled Time</Text></HStack>
                                    </FormLabel>
                                    <TimePicker
                                        value={formData.scheduledTime}
                                        onChange={val => { setFormData(p => ({ ...p, scheduledTime: val })); setFormErrors(p => ({ ...p, scheduledTime: '' })); }}
                                        inputBg={inputBg} borderColor={borderColor} headingColor={headingColor} mutedColor={mutedColor}
                                        isInvalid={!!formErrors.scheduledTime}
                                    />
                                    {formErrors.scheduledTime && <Text fontSize="xs" color="red.500" mt={1}>{formErrors.scheduledTime}</Text>}
                                </FormControl>
                            </VStack>
                        </ModalBody>

                        <ModalFooter borderTop="1px" borderTopColor={borderColor}>
                            <Button colorScheme="blue" mr={3} onClick={handleSave}>
                                {isEditMode ? 'Update Mapping' : 'Create Mapping'}
                            </Button>
                            <Button variant="ghost" onClick={handleFormClose} color={textColor}>Cancel</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/* ── Delete Modal ── */}
                <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="md" isCentered>
                    <ModalOverlay />
                    <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
                            <HStack spacing={3}>
                                <Box color="red.500"><MdDelete size={22} /></Box>
                                <Text>Confirm Deletion</Text>
                            </HStack>
                        </ModalHeader>
                        <ModalCloseButton color={textColor} />
                        <ModalBody py={6}>
                            <Alert status="warning" variant="left-accent" borderRadius="md" mb={4}>
                                <AlertIcon />This action cannot be undone.
                            </Alert>
                            <Text color={textColor} fontSize="sm">
                                Are you sure you want to delete the mapping between&nbsp;
                                <Text as="span" fontWeight="semibold" color={headingColor}>{currentMapping?.clientName}</Text>
                                &nbsp;and checklist&nbsp;
                                <Text as="span" fontWeight="semibold" color={headingColor}>{currentMapping?.checklistName}</Text>?
                            </Text>
                        </ModalBody>
                        <ModalFooter borderTop="1px" borderTopColor={borderColor}>
                            <Button colorScheme="red" mr={3} leftIcon={<MdDelete />} onClick={handleConfirmDelete}>Delete Mapping</Button>
                            <Button variant="ghost" onClick={onDeleteClose} color={textColor}>Cancel</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>

                {/*--View Query --*/}
                <Modal isOpen={isViewOpen} onClose={closeViewQuery} size="4xl" isCentered>
                    <ModalOverlay />
                    <ModalContent bg={cardBg} borderColor={borderColor} borderWidth="1px">
                        <ModalHeader color={headingColor} borderBottom="1px" borderBottomColor={borderColor}>
                            <HStack spacing={3}>
                                <Box color="blue.400"><TbChecklist size={20} /></Box>
                                <Text>View Query</Text>
                            </HStack>
                            <Text fontSize="sm" color={textColor} mt={1}>{viewTitle}</Text>
                        </ModalHeader>
                        <ModalCloseButton color={textColor} />

                        <ModalBody p={0}>
                            <SqlEditor
                                value={viewSql}
                                onChange={() => { }}
                                isReadOnly
                                height="420px"
                                tokens={sqlTokens}
                                toast={toast}
                            />
                        </ModalBody>

                        <ModalFooter borderTop="1px" borderTopColor={borderColor}>
                            <Button onClick={closeViewQuery}>Close</Button>
                        </ModalFooter>
                    </ModalContent>
                </Modal>                          
            </VStack>
        </Box>
    );
}

export default ChecklistMapping;